import { HTTPClientInterface } from '@shared/core/HTTPClientInterface';
import { HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

interface DbAuditLog {
    timestamp: number;
    organizationID: string;
    action: string;
    userID: string;
    changingUser: {
        id: string;
        email: string;
        name: string;
        image: string;
        lastLoginDate: number;
    };
    firestoreRecordId: string;
    firestoreCreationTimestamp: number;
    firestoreUpdateTimestamp: number;
    changes?: DbAuditChange[];
}

interface DbAuditChange {
    field: string;
    oldValue?: unknown;
    newValue?: unknown;
    previousValue?: unknown;
    valueBefore?: unknown;
    valueAfter?: unknown;
}

export interface AuditChange {
    field: string;
    oldValue: string | null;
    newValue: string | null;
}

export interface AuditLog {
    id: string;
    action: string;
    fecha: string;
    hora: string;
    tipo: string;
    responsable: string;
    changes?: AuditChange[];
}

function formatAuditAction(action: string): string {
    switch (action.toLowerCase()) {
        case "create":
            return "Creación";
        case "edit":
            return "Edición";
        case "deactivate":
            return "Desactivación";
        case "reactivate":
            return "Reactivación";
        default:
            return action;
    }
}

function formatAuditResponsible(action: string, responsibleName: string): string {
    const normalizedAction = action.trim().toLowerCase();
    const normalizedResponsibleName = responsibleName
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");

    if (normalizedAction === "create" && normalizedResponsibleName === "joseteloarreglo") {
        return "Sistema";
    }

    return responsibleName;
}

function normalizeAuditChangeValue(value: unknown): string | null {
    if (value === undefined || value === null) {
        return null;
    }

    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch {
        return null;
    }
}

function convertDbAuditChangeToAuditChange(dbAuditChange: DbAuditChange): AuditChange {
    return {
        field: dbAuditChange.field,
        oldValue: normalizeAuditChangeValue(
            dbAuditChange.oldValue ?? dbAuditChange.previousValue ?? dbAuditChange.valueBefore,
        ),
        newValue: normalizeAuditChangeValue(
            dbAuditChange.newValue ?? dbAuditChange.valueAfter,
        ),
    };
}

function convertDbAuditLogToAuditLog(dbAuditLog: DbAuditLog): AuditLog {
    const date = new Date(dbAuditLog.timestamp);
    const fecha = date.toLocaleDateString('es-ES');
    const hora = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    return {
        id: dbAuditLog.firestoreRecordId,
        action: dbAuditLog.action,
        fecha: fecha,
        hora: hora,
        tipo: formatAuditAction(dbAuditLog.action),
        responsable: formatAuditResponsible(dbAuditLog.action, dbAuditLog.changingUser.name),
        changes: (dbAuditLog.changes ?? []).map(convertDbAuditChangeToAuditChange),
    };
}

export class AuditService {
	private httpClient: HTTPClientInterface;

	constructor(httpClient: HTTPClientInterface) {
		this.httpClient = httpClient;
	}

    async getAuditLogs(colabID: string, org: string): Promise<AuditLog[]> {
		const request = new HTTPRequest();
        request.method = 'GET'
        request.url = buildURL(SystemURL.Audit.getAuditLogs,
            {
                colab_id: colabID,
                org_id: org
            }
        )
        const response = await this.httpClient.execute(request);

        const dbAuditLogs: DbAuditLog[] = response.result;
        return dbAuditLogs.map(dbLog => convertDbAuditLogToAuditLog(dbLog));
    }
}
